import { useState } from "react";
import { UserConfig } from "app/types";
import i18n from "features/parent/i18n";
import { AppUser, UserType } from "features/user/types";
import { ParentSettingsTab, PARENT_SETTINGS_TAB_OPTIONS } from "./playerTabUtils";

const getLocaleLabel = (locale?: string | null): string => {
  switch (locale) {
    case "ja_kanji":
      return i18n.t("MSG_PARENT_PAGE_KANJI");
    case "en":
      return i18n.t("MSG_PARENT_PAGE_LANGUAGE_EN");
    case "zh_tw":
    case "zh_cn":
      return i18n.t("MSG_PARENT_PAGE_LANGUAGE_ZH_TW");
    case "ja":
    default:
      return i18n.t("MSG_PARENT_PAGE_HIRAGANA");
  }
};

interface SettingsSectionProps {
  selectedUser: AppUser;
  userConfig: UserConfig | undefined;
  onEditLanguage: () => void;
  onTogglePublish: () => void;
  onToggleCopy: () => void;
  onToggleAiChat: () => void;
  onToggleAiGeneration: () => void;
  onTogglePiecePublish: () => void;
  onTogglePieceCopy: () => void;
  onToggleComment: () => void;
  onToggleShakeBugReport: () => void;
  onChangeUserStatus: () => void;
}

// 設定の1行（アイコン＋ラベル＋ON/OFF＋トグル）
/** 設定1行の高さ。タブ間で高さを変えないための計算に使う */
const SETTING_ROW_HEIGHT = 94;

function ToggleRow({
  icon,
  iconColorClass,
  iconBgClass,
  label,
  value,
  onToggle,
}: {
  icon: string;
  iconColorClass: string;
  iconBgClass: string;
  label: string;
  value: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex-row-el items-center gap-3 py-3 border-b border-outline overflow-hidden">
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBgClass}`}
      >
        <span className={`material-symbols-outlined type-md ${iconColorClass}`}>{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="type-sm text-gray2-100 mb-1">{label}</div>
        <div className={`type-body ${value ? "text-green-100" : "text-gray2-100"}`}>
          {value ? "ON" : "OFF"}
        </div>
      </div>
      <button
        onClick={onToggle}
        className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
          value ? "bg-green-100" : "bg-gray2-100"
        }`}
      >
        <div
          className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
            value ? "translate-x-[24px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

export function SettingsSection({
  selectedUser,
  userConfig,
  onEditLanguage,
  onTogglePublish,
  onToggleCopy,
  onToggleAiChat,
  onToggleAiGeneration,
  onTogglePiecePublish,
  onTogglePieceCopy,
  onToggleComment,
  onToggleShakeBugReport,
  onChangeUserStatus,
}: SettingsSectionProps) {
  // 項目が増えて縦に長くなったため、基本情報と同じくタブで分ける
  const [tab, setTab] = useState<ParentSettingsTab>(ParentSettingsTab.BASIC);
  const maxRows = window.ReactNativeWebView ? 5 : 4;

  return (
    <div className="flex-col-el rounded-xl p-6">
      {/* 見出しは「基本情報」「最近の活動」と同じ指定にそろえる（中央だけ下がって見えていた） */}
      <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black mb-5 pb-3 border-b border-outline">
        <span className="material-symbols-outlined !text-[45px] text-red-100">tune</span>
        {i18n.t("MSG_PARENT_PAGE_SETTINGS")}
      </div>

      {/* カテゴリタブ（基本情報タブと同じ見た目） */}
      <div className="flex-row-el items-center gap-2 mb-4">
        {PARENT_SETTINGS_TAB_OPTIONS.map((option) => (
          <button
            key={option.key}
            onClick={() => setTab(option.key)}
            className={`px-3 py-2 rounded-xl type-sm border-none transition-all cursor-pointer shrink-0 whitespace-nowrap ${
              tab === option.key
                ? "bg-ink text-white"
                : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
            }`}
          >
            {i18n.t(option.labelKey)}
          </button>
        ))}
      </div>

      {/* タブを切りかえても高さが変わらないように、いちばん項目が多いタブのぶんを確保する。
          1行は 94px でそろっている（基本=4行、アプリ内は「ふって報告」が増えて5行） */}
      <div className="flex-col-el" style={{ minHeight: SETTING_ROW_HEIGHT * maxRows }}>
        {tab === ParentSettingsTab.BASIC && (
          <>
            <div className="flex-row-el items-center gap-3 py-3 border-b border-outline overflow-hidden">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-purple-100/20">
                <span className="material-symbols-outlined type-md text-purple-100">translate</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="type-sm text-gray2-100 mb-1">
                  {i18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
                </div>
                <div className="type-body text-textcolor-black">
                  {getLocaleLabel(userConfig?.locale)}
                </div>
              </div>
              <button
                onClick={onEditLanguage}
                className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1 px-3 type-sm cursor-pointer hover:bg-gray2-20 transition-all"
              >
                {i18n.t("MSG_PARENT_PAGE_CHANGE")}
              </button>
            </div>

            <ToggleRow
              icon="smart_toy"
              iconColorClass="text-teal-500"
              iconBgClass="bg-teal-500/20"
              label={i18n.t("MSG_PARENT_PAGE_AI_CHAT")}
              value={!!userConfig?.ai_chat}
              onToggle={onToggleAiChat}
            />
            <ToggleRow
              icon="auto_awesome"
              iconColorClass="text-violet-500"
              iconBgClass="bg-violet-500/20"
              label={i18n.t("MSG_PARENT_PAGE_AI_GENERATION")}
              value={!!userConfig?.ai_generation}
              onToggle={onToggleAiGeneration}
            />

            {/* 端末を振って報告する設定。アプリの中でしか振る操作を検知できないので web では出さない */}
            {window.ReactNativeWebView && (
              <ToggleRow
                icon="bug_report"
                iconColorClass="text-blue-100"
                iconBgClass="bg-blue-100/20"
                label={i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT")}
                value={!!userConfig?.shake_bug_report}
                onToggle={onToggleShakeBugReport}
              />
            )}

            <div className="flex-row-el items-center gap-3 py-3">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                  selectedUser.type !== UserType.FROZEN ? "bg-green-60/20" : "bg-danger/20"
                }`}
              >
                <span
                  className={`material-symbols-outlined type-md ${
                    selectedUser.type !== UserType.FROZEN ? "text-green-100" : "text-danger"
                  }`}
                >
                  {selectedUser.type !== UserType.FROZEN ? "check_circle" : "block"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="type-sm text-gray2-100 mb-1">
                  {i18n.t("MSG_PARENT_PAGE_ACCOUNT_STATUS")}
                </div>
                <div
                  className={`type-body ${
                    selectedUser.type !== UserType.FROZEN ? "text-green-100" : "text-danger"
                  }`}
                >
                  {selectedUser.type !== UserType.FROZEN
                    ? i18n.t("MSG_PARENT_PAGE_PLAYABLE")
                    : i18n.t("MSG_PARENT_PAGE_SUSPENDED")}
                </div>
              </div>
              <button
                onClick={onChangeUserStatus}
                className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
                  selectedUser.type !== UserType.FROZEN ? "bg-green-100" : "bg-gray2-100"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
                    selectedUser.type !== UserType.FROZEN ? "translate-x-[24px]" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          </>
        )}

        {tab === ParentSettingsTab.CREATOR && (
          <>
            <ToggleRow
              icon="public"
              iconColorClass="text-orange-100"
              iconBgClass="bg-orange-100/20"
              label={i18n.t("MSG_PARENT_PAGE_PROJECT_PUBLISH")}
              value={!!userConfig?.project_publish}
              onToggle={onTogglePublish}
            />
            <ToggleRow
              icon="content_copy"
              iconColorClass="text-cyan-100"
              iconBgClass="bg-cyan-100/20"
              label={i18n.t("MSG_PARENT_PAGE_COPY_PERMISSION")}
              value={!!userConfig?.project_copied}
              onToggle={onToggleCopy}
            />
          </>
        )}

        {tab === ParentSettingsTab.PIECE && (
          <>
            <ToggleRow
              icon="public"
              iconColorClass="text-orange-100"
              iconBgClass="bg-orange-100/20"
              label={i18n.t("MSG_PARENT_PAGE_PIECE_PUBLISH")}
              value={!!userConfig?.piece_publish}
              onToggle={onTogglePiecePublish}
            />
            <ToggleRow
              icon="content_copy"
              iconColorClass="text-cyan-100"
              iconBgClass="bg-cyan-100/20"
              label={i18n.t("MSG_PARENT_PAGE_PIECE_COPY")}
              value={!!userConfig?.piece_copy}
              onToggle={onTogglePieceCopy}
            />
            <ToggleRow
              icon="chat_bubble"
              iconColorClass="text-blue-100"
              iconBgClass="bg-blue-100/20"
              label={i18n.t("MSG_PARENT_PAGE_PIECE_COMMENT")}
              value={!!userConfig?.comment_enabled}
              onToggle={onToggleComment}
            />
          </>
        )}
      </div>
    </div>
  );
}
